import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Button,
  ProgressStepper,
  Step,
  StepButton,
  StepLabel,
  Alert,
} from '@one-thd/sui-atomic-components';
import { RadioButtonUnchecked, InfoFilled } from '@one-thd/sui-icons';
import { SweepstakesMarkdown } from '../../../utils/SweepstakesMarkdown';
import { SWEEPSTAKES_DETAILS_CONTENT } from '../../../../../core/Data/SweepstakesMockData';
import { windowExists } from '../../../../../common/utils/AccountUtils';
import { useImageError } from '../../../utils/SweepstakesUtils';
import { RenderCompletionAlert } from '../../SweepstakesUtils';

// Utility functions
const formatExpirationDate = (dateString) => {
  if (!dateString) return '';

  const expirationDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expirationDate.setHours(0, 0, 0, 0);

  if (expirationDate.getTime() === today.getTime()) {
    return 'Ends Today';
  }

  return `Ends ${expirationDate.toLocaleDateString()}`;
};

const handleStepClick = (step, sweepstakesData) => {
  if (windowExists()) {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-step-drawer', {
      selectedStep: step,
      sweepstakesData,
    });
  }
};

const handleFirstStepClick = (step, sweepstakesData) => {
  if (windowExists()) {
    // If first step is not completed, open opt-in drawer instead of step drawer
    if (!step.completed) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-opt-in-drawer', {
        sweepstakesData,
      });
    }
    // If completed, do nothing (button is disabled)
  }
};

// Render components
const RenderStepButton = ({
  step, index, isOptedIn, onStepClick, onFirstStepClick
}) => {
  const stepIcon = useImageError();
  const isFirstStep = index === 0;

  // Determine if button should be clickable
  // First step: clickable only if opted in and not completed
  // Other steps: always clickable
  const isClickable = isFirstStep
    ? (isOptedIn && !step.completed)
    : true;

  const clickFunction = isFirstStep ? () => onFirstStepClick(step) : () => onStepClick(step);
  const handleClick = isClickable ? clickFunction : undefined;

  return (
    <Step key={step.id} completed={step.completed}>
      <div data-testid={`step-button-${step.id}`}>
        <StepButton
          onClick={handleClick}
          icon={() => (
            stepIcon.hasError ? (
              <RadioButtonUnchecked />
            ) : (
              <img
                src={step.iconUrl}
                alt={`Step ${index + 1}`}
                width={28}
                height={28}
                className="sui-rounded-full"
                onError={stepIcon.handleError}
              />
            )
          )}
        />
      </div>
      <StepLabel>
        <div>
          <Typography
            variant="body-base"
            decoration={isClickable ? 'underline' : undefined}
            onClick={handleClick}
          >
            {step.title}
          </Typography>
          {/* Hide subtitle for completed steps - uncomment when ready to use */}
          {step.subtitle && !step.completed && (
            <div className="sui-mt-1">
              <Typography variant="body-sm" color="subtle">
                {step.subtitle}
              </Typography>
            </div>
          )}
        </div>
      </StepLabel>
    </Step>
  );
};

const RenderStepsSection = ({ steps, isOptedIn, onStepClick, onFirstStepClick }) => {
  if (!steps || steps.length === 0) return null;

  return (
    <div>
      <div className="sui-mb-4">
        <Typography variant="body-lg" weight="bold">
          {SWEEPSTAKES_DETAILS_CONTENT.stepsHeader}
        </Typography>
      </div>
      <ProgressStepper
        centerEdgeNodes
        nonLinear
        orientation="vertical"
        connector={<></>}
      >
        {steps.map((step, index) => (
          <RenderStepButton
            key={step.id}
            step={step}
            index={index}
            isOptedIn={isOptedIn}
            onStepClick={onStepClick}
            onFirstStepClick={onFirstStepClick}
          />
        ))}
      </ProgressStepper>
    </div>
  );
};

const RenderExpirationInfo = ({ expirationDate }) => {
  if (!expirationDate) return null;

  return (
    <div
      className="sui-flex sui-items-center sui-gap-3 sui-p-3 sui-rounded sui-bg-info-subtle"
      data-testid="sweepstakes-expiration-info"
    >
      <InfoFilled color="info" size="small" />
      <div className="sui-flex-1">
        <Typography variant="body-sm">
          {formatExpirationDate(expirationDate)}
        </Typography>
      </div>
    </div>
  );
};

const RenderLearnMore = ({ learnMoreText }) => {
  if (!learnMoreText) return null;

  // Extract URL from markdown to check if user is already on that page
  const linkMatch = learnMoreText.match(/\[([^\]]+)\]\(([^)]+)\)/);
  if (linkMatch && windowExists()) {
    const linkUrl = linkMatch[2];
    try {
      // Get the full current URL including query params
      const currentUrl = window.location.href;
      const currentPath = window.location.pathname;

      // Parse the link URL to get its pathname
      const linkPath = new URL(linkUrl, window.location.origin).pathname;

      // Don't show if current URL contains the link URL or if pathnames match
      if (currentUrl.includes(linkUrl) || currentPath === linkPath) {
        return null;
      }
    } catch (error) {
      // If URL parsing fails, still show the component
    }
  }

  return (
    <div data-testid="sweepstakes-learn-more">
      <SweepstakesMarkdown markdownText={learnMoreText} openLinksInNewTab />
    </div>
  );
};

const RenderTermsAndConditions = ({ termsAndConditions }) => {
  if (!termsAndConditions) return null;

  return (
    <div>
      <SweepstakesMarkdown markdownText={termsAndConditions} />
    </div>
  );
};

const RenderFooter = ({ sweepstakesData }) => {
  if (!sweepstakesData?.ctaTitle) return null;

  return (
    <Button
      variant="primary"
      fullWidth
      data-testid="sweepstakes-details-cta-button"
    >
      {sweepstakesData.ctaTitle}
    </Button>
  );
};

export const DetailsContent = ({ sweepstakesData }) => {
  const onStepClick = React.useCallback((step) => {
    handleStepClick(step, sweepstakesData);
  }, [sweepstakesData]);

  const onFirstStepClick = React.useCallback((step) => {
    handleFirstStepClick(step, sweepstakesData);
  }, [sweepstakesData]);

  const content = (
    <div className="sui-space-y-6" data-testid="sweepstakes-details-content">
      <div>
        <SweepstakesMarkdown markdownText={SWEEPSTAKES_DETAILS_CONTENT.initialParagraph} />
      </div>
      <RenderStepsSection
        steps={sweepstakesData?.steps}
        isOptedIn={sweepstakesData?.isOptedIn}
        onStepClick={onStepClick}
        onFirstStepClick={onFirstStepClick}
      />
      <RenderCompletionAlert />
      <RenderExpirationInfo expirationDate={sweepstakesData?.expirationDate} />
      <RenderLearnMore learnMoreText={sweepstakesData?.stepTrackerLearnMoreText} />
      <RenderTermsAndConditions termsAndConditions={sweepstakesData?.termsAndConditions} />
    </div>
  );

  const footer = <RenderFooter sweepstakesData={sweepstakesData} />;

  return { content, footer };
};

DetailsContent.propTypes = {
  sweepstakesData: PropTypes.shape({
    title: PropTypes.string,
    logo: PropTypes.string,
    ctaTitle: PropTypes.string,
    expirationDate: PropTypes.string,
    termsAndConditions: PropTypes.string,
    stepTrackerLearnMoreText: PropTypes.string,
    steps: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      subtitle: PropTypes.string,
      iconUrl: PropTypes.string,
      completed: PropTypes.bool,
    })),
  }),
};

DetailsContent.defaultProps = {
  sweepstakesData: null,
};